/**
 * Cooling System descriptor-engine save bridge (#997 Story 6.6) — the Cooling
 * instantiation of the generic `useRepeatableEngineSaveBridge` (mirror of
 * `useLargeUpsEngineSaveBridge`).
 *
 * Replaces the parent-owned `useCoolingSystemEditSession` for the persisted
 * (Request Detail) path once the Cooling step runs on `DescriptorEngineSection`.
 * Supplies the COOLING bindings (query hook, flush, canonical content-key
 * comparator, invalidation keys, legacy synthesis, editedData key) and renames
 * the generic handle to the unchanged bespoke
 * `CoolingSystemConfigurationsSaveHandle` contract so every existing call site
 * keeps working. The flush's created-id map is surfaced through
 * `flushPendingCoolingSystemChanges` for the Obs 6 staged-upload remap.
 *
 * See `useRepeatableEngineSaveBridge` for the held-baseline / same-chain /
 * reset-to-flush-result semantics (the 6-3 review-patch cluster).
 */

import { useMemo } from 'react';

import {
  coolingSystemConfigurationsKeys,
  useCoolingSystemConfigurations,
} from './queries/useCoolingSystemConfigurations';
import {
  useRepeatableEngineSaveBridge,
  type AggregateSaveBridgeExtras,
} from './useRepeatableEngineSaveBridge';
import { buildLegacyCoolingSystemFromRequestFields } from '../components/coolingsystems/coolingSystemListUtils';
import {
  coolingSystemEditSessionHasChanges,
  flushCoolingSystemEditSession,
} from '../services/coolingSystemEditSessionFlush';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type { CoolingSystemConfigurationsSaveHandle } from '../request/steps/cooling/subsections/coolingSystemConfigurationsSaveHandle';
import type { CoolingSystemConfiguration } from '../types/coolingSystem';

export type UseCoolingSystemEngineSaveBridgeParams = {
  requestId: string | undefined;
  isEditing: boolean;
  isOfflineRequest: boolean;
  /** Flat request fields (request + editedData merge) — legacy unit synthesis. */
  legacyRequestFields?: Record<string, unknown>;
  /** The request payload's PERSISTED `coolingSystemConfigurations` embed. */
  persistedEmbedCoolingSystems?: unknown;
  /** Live session list — `editedData.coolingSystemConfigurations` (engine bridge writes it). */
  editedCoolingSystemConfigurations?: unknown;
  /** True when Cooling was touched this session (`dirtyFields.has('coolingSystemConfigurations')`). */
  coolingSystemConfigurationsDirty: boolean;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  restoredBaseline?: CoolingSystemConfiguration[] | null;
  onDirtyChange?: (dirty: boolean) => void;
};

export function useCoolingSystemEngineSaveBridge({
  requestId,
  isEditing,
  isOfflineRequest,
  legacyRequestFields,
  persistedEmbedCoolingSystems,
  editedCoolingSystemConfigurations,
  coolingSystemConfigurationsDirty,
  applyEditedDataPatch,
  restoredBaseline,
  onDirtyChange,
}: UseCoolingSystemEngineSaveBridgeParams): {
  saveHandle:
    | (CoolingSystemConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null;
  registerInstanceSaveHandle: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  /** The held edit-session baseline (for draft-snapshot persistence). */
  sessionBaseline: CoolingSystemConfiguration[] | null;
} {
  const {
    saveHandle: genericHandle,
    registerInstanceSaveHandle,
    sessionBaseline,
  } = useRepeatableEngineSaveBridge<CoolingSystemConfiguration>({
    requestId,
    isEditing,
    isOfflineRequest,
    legacyRequestFields,
    persistedEmbedInstances: persistedEmbedCoolingSystems,
    editedInstances: editedCoolingSystemConfigurations,
    instancesDirty: coolingSystemConfigurationsDirty,
    applyEditedDataPatch,
    restoredBaseline,
    onDirtyChange,
    editedDataKey: 'coolingSystemConfigurations',
    useInstancesQuery: useCoolingSystemConfigurations,
    hasChanges: coolingSystemEditSessionHasChanges,
    flush: flushCoolingSystemEditSession,
    invalidateListKeys: id => [
      coolingSystemConfigurationsKeys.list(id),
      coolingSystemConfigurationsKeys.grouped(id),
    ],
    buildLegacyFromRequestFields: buildLegacyCoolingSystemFromRequestFields,
  });

  const saveHandle = useMemo(():
    | (CoolingSystemConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null => {
    if (!genericHandle) {
      return null;
    }
    return {
      isCoolingSystemEditorOpen: genericHandle.isEditorOpen,
      hasUnsavedCoolingSystemEditor: genericHandle.hasUnsavedEditor,
      commitOpenCoolingSystemEditor: genericHandle.commitOpenEditor,
      saveUnsavedCoolingSystemIfNeeded: genericHandle.saveUnsavedIfNeeded,
      hasPendingCoolingSystemChanges: genericHandle.hasPendingChanges,
      flushPendingCoolingSystemChanges: genericHandle.flushPendingChanges,
      tryDismissCoolingSystemEditor: genericHandle.tryDismissEditor,
      discardOpenCoolingSystemEditor: genericHandle.discardOpenEditor,
      // #1501 aggregate save seams (uniform names across all six families)
      getPendingDiff: genericHandle.getPendingDiff,
      absorbExternalFlushResult: genericHandle.absorbExternalFlushResult,
      remapSessionIds: genericHandle.remapSessionIds,
      resetSessionAfterScopeRemoval:
        genericHandle.resetSessionAfterScopeRemoval,
    };
  }, [genericHandle]);

  return { saveHandle, registerInstanceSaveHandle, sessionBaseline };
}
